import React, { memo, useState, useRef, useEffect } from 'react';
import classnames from 'classnames';
import { useHistory } from 'react-router-dom';
import { Row, Col, Button, Empty, Tooltip, Select, Icon } from 'antd';
import { Form } from '@ant-design/compatible';
import {
  SettingOutlined,
  PlusOutlined,
  EditOutlined,
  ExclamationCircleFilled,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { useQuery } from '@apollo/client';

import * as queries from '~settings/pms-queries';
import generatePath from '~client/settings/routing';
import { bookingJourneyType } from '~client/constants';
import { propertyStatus } from '~constants/listing-management';
import { DEFAULT_ENUM_DUE_HOURS, paymentPlanDueHours } from '~constants/payment';
import { QUERY_PAYMENT_PLANS } from '~pages/pms-dashboard/properties/queries';
import { listingManagementFetch } from '~helpers/graphql';

import { IconWallet } from '~components/svgs/';
import PropertyDetailWrapper from '~components/property-detail-wrapper';
import SelectPaymentPlan from './modal';
import PaymentPlanDetail from './detail';

const PaymentPlan = memo(({ t, property, form, setProperty, afterSave, allowNoPayment }) => {
  const { getFieldDecorator, validateFieldsAndScroll } = form;

  const history = useHistory();
  const selectPaymentPlanRef = useRef(null);

  const [currentPaymentPlan, setCurrentPaymentPlan] = useState(property?.paymentPlan);
  const variables =
    property?.apCategory === 'STANDARD'
      ? {
          currency: property.currency,
          pageSize: 100,
          pageNumber: 1,
          category: 'RENT_IS_REQUIRED',
        }
      : {
          currency: property.currency,
          pageSize: 100,
          pageNumber: 1,
        };
  const { loading, data } = useQuery(QUERY_PAYMENT_PLANS, {
    variables,
  });

  const hasPaymentPlan = data?.paymentPlans?.totalCount > 0;

  const handleChangePaymentPlan = () => {
    selectPaymentPlanRef?.current?.handleOpenModal(property?.paymentPlan?.id);
  };

  const handleSelectPaymentPlan = () => {
    selectPaymentPlanRef?.current?.handleOpenModal();
  };

  const handleGotoPaymentPlan = () => {
    history.push(generatePath('paymentList'));
  };

  const handleChangeSuccess = (plan, newProperty) => {
    const newPaymentPlan = { ...plan, hasIntegratedProperty: property.integrated };
    setCurrentPaymentPlan(newPaymentPlan);
    setProperty({
      property: {
        ...newProperty,
        paymentPlan: newPaymentPlan,
        publishCondition: {
          ...newProperty.publishCondition,
          paymentPlan: true,
        },
      },
    });
  };

  const handleSubmit = () => {
    return new Promise((resolve, reject) => {
      validateFieldsAndScroll((err, values) => {
        if (!err) {
          const { paymentDueHours, confirmDueHours } = values;
          const changeParams = {};
          let isUpdate = false;

          if (paymentDueHours !== property.paymentDueHours) {
            Object.assign(changeParams, {
              paymentDueHours,
            });
          }

          if (confirmDueHours !== property.confirmDueHours) {
            Object.assign(changeParams, {
              confirmDueHours,
            });
          }

          if (Object.keys(changeParams).length > 0) {
            listingManagementFetch(
              queries.updatePropertyDueHours({ ...changeParams, id: property.id }),
            )
              .then(res => {
                if (res && res.updatePropertyDetails && res.updatePropertyDetails.property) {
                  Object.assign(changeParams, {
                    drafts: res.updatePropertyDetails.property.drafts,
                  });
                }

                isUpdate = true;
              })
              .catch(e => {
                reject(e);
              });
          }

          if (isUpdate) {
            resolve({ isUpdated: true });
          }

          afterSave({
            ...property,
            ...changeParams,
          });
        } else {
          resolve({ isUpdated: false });
        }
      });
    });
  };

  useEffect(() => {
    setCurrentPaymentPlan(property?.paymentPlan);
  }, [property?.paymentPlan?.id]);

  console.log('property ---->', property);

  return (
    <PropertyDetailWrapper
      t={t}
      isPublished={property.status === propertyStatus.PUBLISHED}
      property={property}
      onClickSave={handleSubmit}
    >
      <div className="payment-plan">
        <If condition={property.bookingJourney === bookingJourneyType.SEMI_AUTOMATIC}>
          <Form className="payment-plan__due-hours" colon={false}>
            <div className="payment-plan__section--title">
              {t('cms:cms.property.listing_management.payment_plan.section_due_hours')}
            </div>

            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  label={
                    <span>
                      {t('landlord.payment.long_tail.initial_setup.label.payment_due_hours')}
                      <Tooltip
                        className="payment-plan__due-hours-tooltip"
                        title={
                          <span
                            dangerouslySetInnerHTML={{
                              __html: t(
                                'landlord.payment.long_tail.initial_setup.tips.payment_due_hours',
                              ),
                            }}
                          />
                        }
                      >
                        <QuestionCircleOutlined className="payment-plan__due-hours-icon" />
                      </Tooltip>
                    </span>
                  }
                >
                  {getFieldDecorator('paymentDueHours', {
                    rules: [{ required: true }],
                    initialValue: property?.paymentDueHours ?? DEFAULT_ENUM_DUE_HOURS,
                  })(
                    <Select>
                      <For each="enumDueHours" of={Object.keys(paymentPlanDueHours)}>
                        <Select.Option value={enumDueHours} key={enumDueHours}>
                          {t('landlord.payment.long_tail.due_hours.lable', {
                            num: paymentPlanDueHours[enumDueHours],
                          })}
                        </Select.Option>
                      </For>
                    </Select>,
                  )}
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={
                    <span>
                      {t('landlord.payment.long_tail.initial_setup.label.confirm_due_hours')}
                      <Tooltip
                        className="payment-plan__due-hours-tooltip"
                        title={
                          <span
                            dangerouslySetInnerHTML={{
                              __html: t(
                                'landlord.payment.long_tail.initial_setup.tips.confirm_due_hours',
                              ),
                            }}
                          />
                        }
                      >
                        <QuestionCircleOutlined className="payment-plan__due-hours-icon" />
                      </Tooltip>
                    </span>
                  }
                >
                  {getFieldDecorator('confirmDueHours', {
                    rules: [{ required: true }],
                    initialValue: property?.confirmDueHours ?? DEFAULT_ENUM_DUE_HOURS,
                  })(
                    <Select>
                      <For each="enumDueHours" of={Object.keys(paymentPlanDueHours)}>
                        <Select.Option value={enumDueHours} key={enumDueHours}>
                          {t('landlord.payment.long_tail.due_hours.lable', {
                            num: paymentPlanDueHours[enumDueHours],
                          })}
                        </Select.Option>
                      </For>
                    </Select>,
                  )}
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </If>

        <div className="payment-plan__content">
          <div className="payment-plan__section--title">
            {t('cms:cms.property.listing_management.payment_plan.section_payment_plan')}
          </div>

          <If condition={property?.integrated && currentPaymentPlan?.hasIntegratedProperty}>
            <div className="payment-plan__tip">
              <Icon type="info-circle" theme="filled" className="info-icon" />
              <div className="tips">
                {t('cms:cms.payment.long_tail.list.has_integrated_property.tip')}
              </div>
            </div>
          </If>

          <Choose>
            <When condition={currentPaymentPlan?.id}>
              <div className="payment-plan__detail">
                <div className="payment-plan__detail--header">
                  <div className="payment-plan__detail--info">
                    <p className="payment-plan__detail--intro">
                      {t('cms:cms.property.listing_management.payment_plan.detail.intro')}
                    </p>
                    <div className="payment-plan__detail--name">
                      <IconWallet />
                      <span>{currentPaymentPlan?.name}</span>
                    </div>
                  </div>
                  <div className="payment-plan__detail--change">
                    <Choose>
                      <When condition={property.status === propertyStatus.PUBLISHED}>
                        <Tooltip
                          title={t(
                            'cms:cms.property.listing_management.payment_plan.detail.change_payment_plan.tooltip',
                          )}
                        >
                          <Button type="link" disabled>
                            <EditOutlined />
                            {t(
                              'cms:cms.property.listing_management.payment_plan.detail.change_payment_plan',
                            )}
                          </Button>
                        </Tooltip>
                      </When>
                      <Otherwise>
                        <Button type="link" onClick={handleChangePaymentPlan}>
                          <EditOutlined />
                          {t(
                            'cms:cms.property.listing_management.payment_plan.detail.change_payment_plan',
                          )}
                        </Button>
                      </Otherwise>
                    </Choose>
                  </div>
                </div>

                <div className="payment-plan__detail--main">
                  <PaymentPlanDetail
                    t={t}
                    paymentPlan={currentPaymentPlan}
                    property={property}
                    handleGotoPaymentPlan={handleGotoPaymentPlan}
                  />
                </div>
              </div>
            </When>
            <Otherwise>
              <div
                className={classnames('payment-plan__empty', {
                  'payment-plan__select': hasPaymentPlan,
                })}
              >
                <If condition={hasPaymentPlan}>
                  <div className="payment-plan__empty--select">
                    <div className="payment-plan__empty--select-warn">
                      <ExclamationCircleFilled className="payment-plan__empty--select-icon" />
                      <span className="payment-plan__empty--select-text">
                        {t('cms:cms.property.listing_management.payment_plan.empty.create_warning')}
                      </span>
                    </div>
                    <Button onClick={handleSelectPaymentPlan}>
                      <PlusOutlined />
                      {t(
                        'cms:cms.property.listing_management.payment_plan.empty.select_button_text',
                      )}
                    </Button>
                  </div>
                </If>

                <div className="payment-plan__empty--wrapper">
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={t(
                      `cms:cms.property.listing_management.payment_plan.empty.${
                        hasPaymentPlan ? 'link_notice' : 'create_notice'
                      }`,
                    )}
                  >
                    <If condition={!hasPaymentPlan}>
                      <Button onClick={handleGotoPaymentPlan}>
                        <SettingOutlined />
                        {t(
                          'cms:cms.property.listing_management.payment_plan.empty.goto_button_text',
                        )}
                      </Button>
                    </If>
                  </Empty>
                </div>
              </div>
            </Otherwise>
          </Choose>
        </div>

        <SelectPaymentPlan
          t={t}
          property={property}
          ref={selectPaymentPlanRef}
          loading={loading}
          propertyId={property.id}
          paymentPlans={data?.paymentPlans?.edges}
          onChangeSuccess={handleChangeSuccess}
          allowNoPayment={allowNoPayment}
        />
      </div>
    </PropertyDetailWrapper>
  );
});

export default Form.create({ name: 'PaymentPlan' })(PaymentPlan);
